import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CoachModeProvider, useCoachMode } from "./CoachModeContext";
import { awaitingCoachVerification } from "../../auth/ProtectedRoute";
import { COACH_VERIFICATION_PATH } from "./coachPaths";
import { logout } from "../../../store/actions/userActions";
import wordmark from "../../../assets/brand/cache-wordmark.svg";
import iconMail from "../../../assets/brand/icon-mail.svg";
import { initialsOf } from "../../ForBrands/brandDealUtils";
import { useFitScale, fitScaleStyle } from "../../ForBrands/useFitScale";
import { PhoneThemeProvider, usePhoneTheme } from "../../ForBrands/useIsPhone";
import { MobileBottomNav, MobileTopBar, NAV_ICONS } from "../../ForBrands/ui/mobile/MobileShell";
import "../../ForBrands/brandMode.css";

// Coach Mode shell (Figma "AppShell / Coach"): the shared 72px topbar and
// tab bar with the COACH MODE label. The sections come first (Dashboard,
// Team, My Circle), then CacheShare™ in the Tools group: it is a tool, not a
// section. The global Navbar and Sidebar are hidden on /coach routes. The
// Dashboard tab opens the Home page, whose route stays /coach/home.
//
// While a coach awaits admin verification, Team and CacheShare™ are locked
// (disabled, no href), and Dashboard and My Circle lead to the awaiting
// screen, which is all ProtectedRoute lets them open anyway.
//
// An athlete's profile (/coach/athletes/:athleteId) opens from the roster,
// so the Team tab stays active there.
//
// Phones (below 640px) get the COACH MODE V2 mobile chrome instead (Figma
// AppShell · Mobile / Coach 2406:746, BottomNav / Coach 2406:1089), picked
// per route by phoneChromeFor below. Tablets and desktops keep the header
// and tab row.

const HOME_PATH = "/coach/home";
const GUTTER = "px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]";

const TEAM_MATCH = ["/coach/team", "/coach/athletes"];
const CACHESHARE_MATCH = ["/coach/cacheshare", "/coach/allocation"];
const CIRCLE_MATCH = ["/coach/circle"];

const SECTION_TABS = [
  { path: HOME_PATH, label: "Dashboard", match: [HOME_PATH], landing: true },
  { path: "/coach/team", label: "Team", match: TEAM_MATCH, lockWhilePending: true },
  { path: "/coach/circle", label: "My Circle", match: CIRCLE_MATCH },
];

const TOOL_TABS = [
  { path: "/coach/cacheshare", label: "CacheShare™", match: CACHESHARE_MATCH, lockWhilePending: true },
];

// The phone bottom nav in Figma's order: CacheShare sits with the sections
// (no ™), My Circle reads Circle, and Home reads Dashboard as on the
// desktop tab row. A tab is current on the same paths as its desktop tab.
const PHONE_TABS = [
  { key: "home", to: HOME_PATH, label: "Dashboard", icon: NAV_ICONS.home, match: [HOME_PATH] },
  { key: "team", to: "/coach/team", label: "Team", icon: NAV_ICONS.team, match: TEAM_MATCH },
  { key: "cacheshare", to: "/coach/cacheshare", label: "CacheShare", icon: NAV_ICONS.cacheshare, match: CACHESHARE_MATCH },
  { key: "circle", to: "/coach/circle", label: "Circle", icon: NAV_ICONS.circle, match: CIRCLE_MATCH },
];

// Pages that draw their own stack header (back, title, Help): the profile
// hub and its sections.
const PHONE_STACK = [/^\/coach\/profile(\/|$)/];

// The chrome a route gets on a phone:
//  - "stack": none; the page renders MobileStackHeader itself.
//  - "top": the top bar only, on every other route while verification is
//    pending. Each tab would be locked or lead back to the awaiting screen,
//    so the bottom nav is left out (Figma 2466:19259 draws an empty row), and
//    so is the mail link, which only points at that screen. The avatar menu
//    keeps Billing, Password and Log out in reach.
//  - "tabs": the top bar and the bottom nav.
const phoneChromeFor = (pathname, pending) => {
  if (PHONE_STACK.some((route) => route.test(pathname || ""))) return "stack";
  return pending ? "top" : "tabs";
};

// The page area on a phone: the 24px gutter, and room at the bottom for the
// fixed 67px nav and the home indicator. Stack pages own their full-bleed
// strips and pad their own body.
//
// In the top-only chrome the shell is a full-height column: the root is at
// least the screen's height (100dvh, or 100vh in browsers without dynamic
// viewport units, such as iOS Safari before 15.4) and main takes what the top
// bar leaves as a flex column. A page fills the screen with flex-1 and pins a
// footer to the bottom edge (the awaiting verification screen) without
// knowing the top bar's height.
const PHONE_MAIN = {
  tabs: "w-full px-6 pt-5 pb-[calc(99px+env(safe-area-inset-bottom))]",
  top: "flex w-full flex-1 flex-col px-6 pt-5 pb-8",
  stack: "w-full",
};
const PHONE_ROOT_TOP = "bm-root flex min-h-dvh flex-col bg-white supports-[not(height:100dvh)]:min-h-screen";

// The tab as the signed-in coach sees it. While verification is pending,
// Dashboard and My Circle lead to the awaiting screen (Dashboard shows as
// active there) and the team tools are locked.
const tabFor = (tab, pending) => {
  if (!pending) return tab;
  if (tab.lockWhilePending) return { ...tab, locked: true };
  return { ...tab, path: COACH_VERIFICATION_PATH, match: tab.landing ? [COACH_VERIFICATION_PATH] : [] };
};

// The tab row only renders at 640px and up (phones get the bottom nav). The
// narrow base padding stays so the markup is unchanged where matchMedia is
// missing, which falls back to this row at every width.
const TAB_CLASS = "flex items-center px-2 text-center font-archivo text-[15px] font-bold leading-5 sm:shrink-0 sm:px-6";
const LOCKED_HINT = "Unlocks once an admin verifies your coach account";

const Tab = ({ tab, pathname }) => {
  // A disabled link (no href, not focusable), so assistive technology reads
  // the locked state, with the reason as its description.
  if (tab.locked) {
    return (
      <span role="link" aria-disabled="true" title={LOCKED_HINT} className={`${TAB_CLASS} cursor-not-allowed text-bm-muted`}>
        {tab.label}
      </span>
    );
  }
  const active = tab.match.some((m) => pathname.startsWith(m));
  return (
    <Link
      to={tab.path}
      aria-current={active ? "page" : undefined}
      className={`${TAB_CLASS} ${active ? "border-b-2 border-bm-orange text-bm-orange" : "text-bm-muted hover:text-bm-ink"}`}
    >
      {tab.label}
    </Link>
  );
};

const displayName = (user) => {
  const first = user?.firstName || (user?.name || "").split(" ")[0] || "";
  const last = user?.lastName || user?.surname || ((user?.name || "").split(" ").slice(1).join(" ") || "");
  const full = `${first} ${last}`.trim();
  return full || user?.email || "Coach";
};

const VERIFICATION_LABEL = {
  verified: "Verified coach",
  pending: "Verification pending",
  rejected: "Verification rejected",
};

const MenuLink = ({ to, children }) => (
  <Link to={to} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
    {children}
  </Link>
);

const MENU_CLASS = "bm-fade-in absolute right-0 top-11 z-40 w-60 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl";
const MENU_CLASS_COMPACT = "bm-fade-in absolute right-0 top-10 z-40 w-56 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl";

// `compact` is the phone top bar's form: a 30px avatar with no name beside
// it (the name becomes the button's label) and a narrower dropdown. The
// button pads 5px past the avatar, so the tap target is 40px without moving
// the avatar.
const AvatarMenu = ({ user, pending, compact = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const name = displayName(user);
  const initials = initialsOf(name.split(" ")[0], name.split(" ").slice(1).join(" "));
  const status = user?.coachVerification?.status;

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    // iOS Safari may send no mousedown for a tap on plain content, so the
    // phone menu also closes on a touch outside it (as in Athlete Mode).
    if (compact) document.addEventListener("touchstart", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
    };
  }, [open, compact]);

  // logout() clears the session keys and the persisted store.
  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const size = compact ? "h-[30px] w-[30px]" : "h-8 w-8";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? name : undefined}
        className={compact ? "-m-[5px] flex items-center rounded-full p-[5px]" : "flex items-center gap-4"}
      >
        {!compact && <span className="hidden bm-label-l text-bm-ink sm:inline">{name}</span>}
        {user?.avatar ? (
          <img src={user.avatar} alt="" className={`${size} rounded-full object-cover`} />
        ) : (
          <span className={`flex ${size} items-center justify-center rounded-full bg-bm-orange bm-label-xs text-white`}>{initials}</span>
        )}
      </button>
      {open && (
        <div role="menu" className={compact ? MENU_CLASS_COMPACT : MENU_CLASS}>
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
            {VERIFICATION_LABEL[status] && (
              <div className={`mt-1 bm-label-xs ${status === "verified" ? "text-bm-positive" : "text-bm-attention"}`}>
                {VERIFICATION_LABEL[status]}
              </div>
            )}
          </div>
          <div className="border-t border-bm-border" />
          {pending && <MenuLink to={COACH_VERIFICATION_PATH}>Verification status</MenuLink>}
          <MenuLink to="/coach/profile">Profile &amp; verification</MenuLink>
          <MenuLink to="/coach/billing">Billing</MenuLink>
          <MenuLink to="/coach/password">Password</MenuLink>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={handleLogout} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

// Tablet and desktop chrome: the 72px topbar and the tab row.
const DesktopHeader = ({ user, pending, pathname, badges, home }) => (
  <header className="bg-white">
    <div className={`flex h-[72px] w-full items-center justify-between ${GUTTER}`}>
      <Link to={home} className="flex items-center" aria-label="Coach Mode home">
        <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
      </Link>
      <div className="flex items-center gap-4">
        <Link to={pending ? COACH_VERIFICATION_PATH : `${HOME_PATH}#attention`} className="flex items-center gap-[6px]" aria-label="Needs your attention">
          <img src={iconMail} alt="" className="h-5 w-5" />
          {badges.attention > 0 && (
            <span className="flex h-[22px] items-center justify-center rounded-full bg-bm-orange px-[9px] bm-label-xs text-white">{badges.attention}</span>
          )}
        </Link>
        <span className="h-5 w-px bg-bm-border" />
        <span className="hidden bm-overline md:inline">Coach Mode</span>
        <AvatarMenu user={user} pending={pending} />
      </div>
    </div>
    <nav aria-label="Coach Mode" className="border-b border-bm-border">
      <div className={`flex h-[51px] w-full items-stretch overflow-x-auto ${GUTTER}`}>
        {SECTION_TABS.map((tab) => <Tab key={tab.path} tab={tabFor(tab, pending)} pathname={pathname} />)}
        <div role="group" aria-label="Tools" className="flex items-stretch">
          {TOOL_TABS.map((tab) => <Tab key={tab.path} tab={tabFor(tab, pending)} pathname={pathname} />)}
        </div>
      </div>
    </nav>
  </header>
);

// One tree for every width: only the chrome around `main` changes, and
// `main` keeps its place, so the open page keeps its state when a phone
// turns to landscape (640px and up) and back.
const Shell = ({ user, pending }) => {
  const { pathname } = useLocation();
  const { badges } = useCoachMode();
  const fit = useFitScale();
  const phone = usePhoneTheme();
  const home = pending ? COACH_VERIFICATION_PATH : HOME_PATH;
  const chrome = phone ? phoneChromeFor(pathname, pending) : null;

  let top = null;
  if (!phone) {
    top = <DesktopHeader user={user} pending={pending} pathname={pathname} badges={badges} home={home} />;
  } else if (chrome !== "stack") {
    top = (
      <MobileTopBar
        homeTo={home}
        homeLabel="Coach Mode home"
        mail={pending ? null : { to: `${HOME_PATH}#attention`, label: "Needs your attention", count: badges.attention }}
        menu={<AvatarMenu user={user} pending={pending} compact />}
      />
    );
  }

  return (
    <div className={chrome === "top" ? PHONE_ROOT_TOP : "bm-root bg-white"} style={fitScaleStyle(fit)}>
      {top}
      <main className={phone ? PHONE_MAIN[chrome] : `w-full pb-[70px] ${GUTTER}`}>
        <Outlet />
      </main>
      {chrome === "tabs" && <MobileBottomNav label="Coach Mode" tabs={PHONE_TABS} pathname={pathname} />}
    </div>
  );
};

// The phone theme covers the whole Coach Mode tree, the awaiting
// verification screen included; shared pieces read it through
// usePhoneTheme() and only switch below 640px.
const CoachModeLayout = () => {
  const user = useSelector((state) => state.user.user || {});
  const pending = awaitingCoachVerification(user);
  return (
    <PhoneThemeProvider>
      <CoachModeProvider pollBadges={!pending}>
        <Shell user={user} pending={pending} />
      </CoachModeProvider>
    </PhoneThemeProvider>
  );
};

export default CoachModeLayout;
